/** Wall-clock ⇄ instant conversion for the event forms.
 *
 *  The create/edit forms let the leader type a wall-clock time and pick an
 *  IANA zone from TIMEZONE_OPTIONS. The typed time means "this time in the
 *  picked zone", so the conversion to a stored instant must go through that
 *  zone — never through `new Date('YYYY-MM-DDTHH:mm')`, which ECMA-262
 *  interprets in the browser's own zone and silently shifts the meeting for
 *  any leader whose browser zone differs from their selection.
 *
 *  No tz library: the platform's Intl data is the source of truth, via the
 *  standard formatToParts offset probe.
 */

// UTC-equivalent milliseconds of `instant`'s wall-clock reading in `timeZone`,
// minus the instant itself — i.e. the zone's UTC offset at that instant.
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(instant);
  const get = (type: string): number =>
    Number(parts.find((p) => p.type === type)?.value ?? '0');
  // Intl renders midnight as hour "24" under hour12:false in some engines.
  const hour = get('hour') === 24 ? 0 : get('hour');
  const wallAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  );
  return wallAsUtc - instant.getTime();
}

/** Interprets `"YYYY-MM-DDTHH:mm"` as a wall-clock time in `timeZone` and
 *  returns the instant. Two-pass offset probe so DST transitions near the
 *  wall time resolve to the offset actually in force at the result. */
export function wallTimeInZoneToInstant(wall: string, timeZone: string): Date {
  const wallAsUtc = new Date(`${wall}:00.000Z`);
  // Invalid input propagates as an Invalid Date (matching `new Date(wall)`)
  // rather than throwing from the Intl offset probe — callers isNaN-check.
  if (Number.isNaN(wallAsUtc.getTime())) return wallAsUtc;
  const firstGuess = new Date(
    wallAsUtc.getTime() - zoneOffsetMs(wallAsUtc, timeZone),
  );
  return new Date(wallAsUtc.getTime() - zoneOffsetMs(firstGuess, timeZone));
}

/** Formats an instant as the `"YYYY-MM-DDTHH:mm"` wall-clock reading in
 *  `timeZone` — the value shape `<input type="datetime-local">` requires. */
export function instantToWallTimeInZone(iso: string, timeZone: string): string {
  const instant = new Date(iso);
  const shifted = new Date(instant.getTime() + zoneOffsetMs(instant, timeZone));
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${shifted.getUTCFullYear()}-${p(shifted.getUTCMonth() + 1)}-${p(shifted.getUTCDate())}T${p(shifted.getUTCHours())}:${p(shifted.getUTCMinutes())}`;
}
